import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import { fr as frDayPicker } from 'react-day-picker/locale/fr';
import { Box, Typography, styled } from '@mui/material';
import Dialog from './Dialog';
import Button from './Button';
import SvgIcon from './SvgIcon';
import { ReactComponent as UploadedFileIcon } from '../assets/icons/40/file_uploaded.svg';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/24/arrow_left.svg';
import 'react-day-picker/style.css';

const CustomChevron = ({ orientation, size = 24, className, disabled }) => {
  const isRight = orientation === 'right';
  return (
    <Box
      component={ArrowLeftIcon}
      className={className}
      sx={{
        width: size,
        height: size,
        opacity: disabled ? 0.5 : 1,
        transform: isRight ? 'rotate(180deg)' : 'none',
        display: 'inline-block',
        flexShrink: 0,
        '& path': { fill: '#ACACAC' },
      }}
    />
  );
};

const FileDisplay = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderRadius: 12,
  backgroundColor: '#303030',
  marginBottom: 24,
});

const CalendarWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  overflow: 'hidden',
  marginBottom: 24,
  borderRadius: 14,
  background: '#303030',
  '& table': {
    borderSpacing: 0,
  },
  '& .rdp-day': {
    border: 'none',
    padding: 0,
  },
  '& .rdp-outside:not(.rdp-selected)': {
    opacity: 0.3,
  },
  '& .rdp-root': {
    '--rdp-accent-color': '#B9FF66',
    '--rdp-accent-background-color': '#B9FF66',
    '--rdp-day_button-border': 'none',
    '--rdp-selected-border': 'none',
    '--rdp-day-height': '40px',
    '--rdp-day-width': '40px',
    color: '#FFFFFF',
  },
  '& .rdp-day_button': {
    color: '#FFFFFF',
    borderRadius: 20,
  },
  '& .rdp-selected .rdp-day_button': {
    borderRadius: 20,
    backgroundColor: '#B9FF66',
    color: '#141414',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: '#B9FF66',
      color: '#141414',
    },
  },
  '& .rdp-month_caption': {
    margin: 0,
    padding: '12px 16px',
    borderRadius: '6px 6px 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 44,
  },
  '& .rdp-caption_label': {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 16,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .rdp-nav': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  '& .rdp-button_previous, & .rdp-button_next': {
    color: '#ACACAC',
  },
  '& .rdp-chevron': {
    fill: '#ACACAC',
  },
  '& .rdp-weekday': {
    color: '#ACACAC',
    textTransform: 'uppercase',
    fontWeight: 500,
    textAlign: 'center',
  },
  '& .rdp-months': {
    padding: '16px 24px 24px',
  },
});

const ExpirationDateDialog = ({ open, onClose, fileName, onSave, saving }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedDate(null);
    }
  }, [open]);

  const handleSave = useCallback(() => {
    if (!selectedDate) return;
    onSave(selectedDate);
    setSelectedDate(null);
    onClose();
  }, [selectedDate, onSave, onClose]);

  const handleClose = useCallback(() => {
    setSelectedDate(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="DATE D'EXPIRATION"
      closable
      PaperProps={{ sx: { width: 440 } }}
    >
      <FileDisplay>
        <SvgIcon icon={UploadedFileIcon} color="#FFFFFF" size={24} />
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: '#FFFFFF',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName}
        </Typography>
      </FileDisplay>

      <CalendarWrapper>
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          defaultMonth={selectedDate || new Date()}
          showOutsideDays
          locale={frDayPicker}
          navLayout="around"
          components={{ Chevron: CustomChevron }}
          formatters={{
            formatWeekdayName: (weekday) =>
              format(weekday, 'EEE', { locale: fr }).replace(/\.$/, ''),
          }}
        />
      </CalendarWrapper>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        disabled={!selectedDate}
        sx={{
          backgroundColor: selectedDate ? '#B9FF66' : '#303030',
          color: selectedDate ? '#141414' : '#ACACAC',
          textTransform: 'uppercase',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: selectedDate ? '#A8E85C' : '#303030',
          },
          '&.Mui-disabled': {
            backgroundColor: '#303030',
            color: '#ACACAC',
          },
        }}
      >
        {saving ? 'TÉLÉVERSEMENT...' : 'ENREGISTRER'}
      </Button>
    </Dialog>
  );
};

export default ExpirationDateDialog;
